import { ChevronDown, LoaderCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

type PipelineStageSummary = {
  id: number;
  name: string;
  color: string;
  position: number;
  count: number;
};

type PipelineSummary = {
  state: "ok" | "sin_pipeline";
  total: number | null;
  sin_etapa: number | null;
  stages: PipelineStageSummary[];
};

type PropertyStatusCardProps = {
  summary?: PipelineSummary;
  isLoading: boolean;
  hasError: boolean;
  periodDays: number;
  onPeriodChange: (days: number) => void;
  onStageSelect: (stageId: number) => void;
};

const PERIOD_OPTIONS = [
  { days: 7, label: "7 días" },
  { days: 15, label: "15 días" },
  { days: 30, label: "30 días" },
  { days: 90, label: "90 días" },
];

const LOADING_ROWS = ["one", "two", "three", "four", "five", "six", "seven", "eight"];

export default function PropertyStatusCard({
  summary,
  isLoading,
  hasError,
  periodDays,
  onPeriodChange,
  onStageSelect,
}: PropertyStatusCardProps) {
  const periodLabel =
    PERIOD_OPTIONS.find((option) => option.days === periodDays)?.label ?? `${periodDays} días`;
  const stagesWithContacts = summary?.stages?.filter((stage) => stage.count > 0) ?? [];
  const showDistribution =
    !isLoading && !hasError && summary?.state === "ok" && stagesWithContacts.length > 0;
  const distributionLabel = stagesWithContacts
    .map((stage) => `${stage.name}: ${stage.count}`)
    .join(", ");

  const content = (() => {
    if (isLoading) {
      return (
        <div
          role="status"
          aria-label="Cargando resumen del pipeline"
          className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 @min-[360px]:grid-flow-col @min-[360px]:grid-cols-2 @min-[360px]:grid-rows-4"
        >
          {LOADING_ROWS.map((row) => (
            <div key={row} className="flex items-center justify-between gap-3">
              <span className="h-3 w-28 animate-pulse rounded bg-slate-100" />
              <span className="size-4 animate-pulse rounded bg-slate-100" />
            </div>
          ))}
        </div>
      );
    }

    if (hasError) {
      return (
        <p className="mt-6 text-sm text-slate-500">No pudimos cargar el resumen de estatus.</p>
      );
    }

    if (summary?.state === "sin_pipeline") {
      return <p className="mt-6 text-sm text-slate-500">Aún no hay un pipeline configurado.</p>;
    }

    if (!summary?.stages?.length) {
      return <p className="mt-6 text-sm text-slate-500">No hay etapas para mostrar.</p>;
    }

    return (
      <>
        {showDistribution && (
          <TooltipProvider delayDuration={150}>
            <div
              role="group"
              aria-label={`Distribución del pipeline. ${distributionLabel}`}
              className="mt-6 flex h-4 w-full gap-1"
            >
              {stagesWithContacts.map((stage, index) => {
                const isFirst = index === 0;
                const isLast = index === stagesWithContacts.length - 1;
                const rounding =
                  isFirst && isLast
                    ? "rounded-lg"
                    : isFirst
                      ? "rounded-l-lg rounded-r-sm"
                      : isLast
                        ? "rounded-l-sm rounded-r-lg"
                        : "rounded-sm";

                return (
                  <Tooltip key={stage.id}>
                    <TooltipTrigger asChild>
                      <span
                        tabIndex={0}
                        className={`h-full min-w-px cursor-default focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 ${rounding}`}
                        style={{ backgroundColor: stage.color, flex: `${stage.count} 0 0%` }}
                      />
                    </TooltipTrigger>
                    <TooltipContent className="space-y-0.5 bg-slate-900 px-3 py-2 text-white">
                      <div className="flex items-center gap-2 font-semibold">
                        <span
                          aria-hidden="true"
                          className="size-2 shrink-0 rounded-full"
                          style={{ backgroundColor: stage.color }}
                        />
                        <span>{stage.name}</span>
                      </div>
                      <p className="text-slate-200">
                        {stage.count} {stage.count === 1 ? "contacto" : "contactos"}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </TooltipProvider>
        )}
        <ul className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 @min-[360px]:grid-flow-col @min-[360px]:grid-cols-2 @min-[360px]:grid-rows-4">
          {summary.stages.map((stage) => (
            <li key={stage.id}>
              <button
                type="button"
                onClick={() => onStageSelect(stage.id)}
                aria-label={`Ver ${stage.count} contactos en ${stage.name}`}
                className="flex w-full min-w-0 items-center justify-between gap-3 rounded-md py-1 text-left transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
              >
                <span className="flex min-w-0 items-center gap-2.5 text-sm font-medium text-slate-800">
                  <span
                    aria-hidden="true"
                    className="size-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: stage.color }}
                  />
                  <span className="truncate">{stage.name}</span>
                </span>
                <span className="text-sm font-bold tabular-nums text-slate-500">{stage.count}</span>
              </button>
            </li>
          ))}
        </ul>
        {Boolean(summary.sin_etapa) && (
          <p className="mt-4 text-xs text-slate-400">
            {summary.sin_etapa}{" "}
            {summary.sin_etapa === 1 ? "contacto sin etapa" : "contactos sin etapa"}
          </p>
        )}
      </>
    );
  })();

  return (
    <Card className="@container min-h-80 min-w-0 gap-0 rounded-3xl border border-gray-200 p-8 shadow-[0_12px_16px_rgba(15,23,42,0.03)]">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.5px] text-slate-400">
            Pipeline de ventas
          </p>
          <h2 className="mt-1 text-lg font-bold leading-[1.4] text-slate-800">
            Resumen de Estatus
          </h2>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label={`Periodo del resumen: ${periodLabel}`}
              aria-busy={isLoading}
              className="flex h-10 items-center gap-1.5 rounded-lg border border-[#eaeff4] px-3 text-xs font-medium text-[#1a1d1f] hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
            >
              {isLoading && (
                <LoaderCircle aria-hidden="true" className="size-3.5 animate-spin text-slate-400" />
              )}
              {periodLabel}
              <ChevronDown aria-hidden="true" className="size-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {PERIOD_OPTIONS.map((option) => (
              <DropdownMenuItem key={option.days} onSelect={() => onPeriodChange(option.days)}>
                {option.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {content}
    </Card>
  );
}
